import { NumberGrid } from '../mechanics/base/number-grid'
import { DrawAnimationConfig, SCENE_KEYS } from '@Animation/types'
import { NumberSelector } from '@Animation/mechanics/base/number-selector'
import { Background } from '@Animation/mechanics/base/background'
import { store } from '@AppState/redux-store'
import { AnimationActions, ANIMATION_PLAY_STATE } from '@AppState/animation'
import { ExtraDrawAnimation } from '@Animation/game-objects/extra-draw-animation'
import { GAME_SPEED } from '@AppState/app-settings'
import { Foreground } from '@Animation/mechanics/base/foreground'
import { MultiplierAnimation } from '@Animation/mechanics/add-on-multiplier/multiplier-animation'
import { BallsRemainingDisplay } from '@Animation/game-objects/balls-remaining-display'
import { GENERAL_ASSETS } from '@Animation/constants'
import { tweenPromise } from '@Animation/utils'
import { GameController } from '@Animation/game-control'
export class KenoAnimationScene extends Phaser.Scene {
  gameConfig!: DrawAnimationConfig

  background: Background | null = null
  ballsRemainingDisplay: BallsRemainingDisplay | null = null
  extraDrawAnimation: ExtraDrawAnimation | null = null
  foreground: Foreground | null = null
  multiplierAnimation: MultiplierAnimation | null = null
  numberGrid: NumberGrid | null = null
  numberSelector: NumberSelector | null = null
  topLayerNumberGridContainer: Phaser.GameObjects.Container | null = null

  skipToEnd: boolean = false

  constructor() {
    super({ key: SCENE_KEYS.DRAW_ANIMATION })
  }

  preload() {
    store.dispatch(AnimationActions.setPlayState(ANIMATION_PLAY_STATE.GAME_LOADING))

    this.gameConfig.mechanicConfig.assetPreloader(this)
    this.gameConfig.mechanicConfig.extraDrawAnimationConfig?.assetPreloader(this)
    this.gameConfig.mechanicConfig.multiplierAnimationConfig?.assetPreloader(this)

    this.load.image(GENERAL_ASSETS.WHITE_CIRCLE, '/animations/white-circle.png')

    this.load.start()
  }

  init(data: DrawAnimationConfig) {
    this.gameConfig = data

    GameController.setSounds(data.mechanicConfig.audioKeys)

    store.dispatch(AnimationActions.setAddOnPanelState(false))
  }

  async playNewDraw(data: DrawAnimationConfig) {
    store.dispatch(AnimationActions.setPlayState(ANIMATION_PLAY_STATE.GAME_LOADING))

    this.stopAnimation()

    this.scene.restart(data)
  }
  stopAnimation() {
    this.input.removeAllListeners()
    this.time.clearPendingEvents()
    this.tweens.killAll()
    this.tweens.shutdown()

    this.background?.removeAllListeners()
    this.ballsRemainingDisplay?.removeAllListeners()

    this.extraDrawAnimation?.removeAllListeners()
    this.foreground?.removeAllListeners()
    this.multiplierAnimation?.removeAllListeners()
    this.numberGrid?.removeAllListeners()
    this.numberSelector?.removeAllListeners()
    this.topLayerNumberGridContainer?.removeAllListeners()

    this.background?.destroy(true)

    this.ballsRemainingDisplay?.destroy(true)

    this.extraDrawAnimation?.destroy(true)
    this.foreground?.destroy(true)
    this.multiplierAnimation?.destroy(true)
    this.numberGrid?.destroy(true)
    this.numberSelector?.destroy(true)
    this.topLayerNumberGridContainer?.destroy(true)

    this.background = null
    this.ballsRemainingDisplay = null
    this.extraDrawAnimation = null
    this.foreground = null
    this.multiplierAnimation = null
    this.numberGrid = null
    this.numberSelector = null
    this.topLayerNumberGridContainer = null
  }

  async create(config: DrawAnimationConfig) {
    this.gameConfig.mechanicConfig.textureLoader(this)
    this.setGameSpeed(config.settings.gameSpeed)
    await this.createGameObjects()
    if (this.gameConfig.results.drawNumber !== store.getState().animation.result?.drawNumber) {
      console.error(
        `Expected data for draw result ${store.getState().animation.result?.drawNumber}, but animation configured for ${this.gameConfig.results.drawNumber}`,
      )
      store.dispatch(
        AnimationActions.crash(
          `Expected data for draw result ${store.getState().animation.result?.drawNumber}, but animation configured for ${this.gameConfig.results.drawNumber}`,
        ),
      )

      store.dispatch(AnimationActions.setPlayState(ANIMATION_PLAY_STATE.READY))
      this.stopAnimation()
      return
    }
    this.setDepths()
    this.startAnimation()

    store.dispatch(AnimationActions.setPlayState(ANIMATION_PLAY_STATE.PLAYING))
    if (this.gameConfig.settings.startPaused) {
      this.scene.pause()
    }
  }

  setDepths() {
    this.background?.setDepth(10)
    this.numberGrid?.setDepth(20)
    this.numberSelector?.setDepth(30)
    this.foreground?.setDepth(40)
    this.ballsRemainingDisplay?.setDepth(50)
    this.topLayerNumberGridContainer?.setDepth(60)
  }

  async createGameObjects() {
    this.background = new this.gameConfig.mechanicConfig.background(
      this,
      this.cameras.main.centerX,
      this.cameras.main.centerY,
    )
    this.createNumberGrid()
    this.createNumberSelector()
    this.numberGrid?.setName(`Number Grid - Draw ${this.gameConfig.results.drawNumber}`)
    this.createForeground()
    this.createBallsRemainingDisplay()

    this.createExtraDrawAnimation()
    this.createMultiplierAnimation()

    await Promise.all([this.numberGrid?.createNumbers(), this.ballsRemainingDisplay?.createBalls()])

    await tweenPromise(this, {
      targets: [this.numberGrid, this.ballsRemainingDisplay],
      alpha: 1,
      duration: 200,
    })()

    this.topLayerNumberGridContainer = this.add.container(this.numberGrid?.x, this.numberGrid?.y)
  }

  createBallsRemainingDisplay() {
    const extraDraws = this.getExtraAddOnDetails()

    const ballArraySizes = []
    if (extraDraws) {
      ballArraySizes.push(extraDraws.results.length)
    }
    ballArraySizes.push(this.gameConfig.results.winningNumbers.length)

    this.ballsRemainingDisplay = new BallsRemainingDisplay(this, 25, 482, ballArraySizes)
  }

  createForeground() {
    if (!this.gameConfig.mechanicConfig.foreground) {
      return
    }
    this.foreground = new this.gameConfig.mechanicConfig.foreground(this, 0, 0)
  }

  createMultiplierAnimation() {
    const addOnResult = this.getMultiplierAddOnDetails()
    if (
      !addOnResult ||
      !this.gameConfig.mechanicConfig.multiplierAnimationConfig ||
      !this.gameConfig.results.ticketInfo.multiplierAddOn
    ) {
      this.multiplierAnimation = null
      return
    }

    this.multiplierAnimation =
      new this.gameConfig.mechanicConfig.multiplierAnimationConfig.revealAnimation(
        this,
        this.cameras.main.centerX,
        this.cameras.main.centerY,
        {
          result: addOnResult.value,
          possibleResults: this.gameConfig.results.multiplierAddOnResult?.possibleResults ?? [],
        },
      )

    this.multiplierAnimation.setDepth(50)
  }

  createExtraDrawAnimation() {
    const extraDrawDetails = this.getExtraAddOnDetails()

    if (!extraDrawDetails || !this.gameConfig.mechanicConfig.extraDrawAnimationConfig) {
      this.extraDrawAnimation = null
      return
    }

    this.gameConfig.mechanicConfig.extraDrawAnimationConfig.textureLoader(this)

    const onNumberSelectedFunc = (index: number) => {
      store.dispatch(
        AnimationActions.updateDisplayNumberIndex(
          this.gameConfig.results.winningNumbers.length + index,
        ),
      )
    }

    const preNumberSelectedFunc = async () => {
      await this.ballsRemainingDisplay?.removeBall()
    }

    this.extraDrawAnimation = new ExtraDrawAnimation(
      this,
      this.gameConfig.mechanicConfig.extraDrawAnimationConfig,
      extraDrawDetails,
      this.numberGrid!,
      preNumberSelectedFunc,
      onNumberSelectedFunc,
    )
  }

  createNumberSelector() {
    const onNumberSelectedFunc = (index: number) => {
      store.dispatch(AnimationActions.updateDisplayNumberIndex(index))
    }

    const preNumberSelectedFunc = async () => {
      await this.ballsRemainingDisplay?.removeBall()
    }

    this.numberSelector = new this.gameConfig.mechanicConfig.numberSelector(
      this,
      this.numberGrid!,
      this.gameConfig?.results.ticketInfo.miniGameSelectedNumbers ?? [],
      preNumberSelectedFunc,
      onNumberSelectedFunc,
    )
  }

  createNumberGrid() {
    this.numberGrid = new NumberGrid(
      this,
      35,
      93,
      this.gameConfig.results.ticketInfo.baseSelectedNumbers,
      this.gameConfig.mechanicConfig.kenoNumber,
      this.gameConfig.mechanicConfig.extraDrawAnimationConfig
        ? this.gameConfig.mechanicConfig.extraDrawAnimationConfig.kenoNumber
        : null,
    )
  }

  destroy() {
    this.textures.destroy()
    this.anims.destroy()
  }

  async startAnimation() {
    if (!this.gameConfig) return
    if (!this.numberSelector) return

    this.scene.sendToBack(SCENE_KEYS.LOADING)

    this.skipToEnd = false

    this.extraDrawAnimation?.reset()

    await this.multiplierAnimation?.play()
    this.multiplierAnimation = null
    store.dispatch(AnimationActions.setAddOnPanelState(true))

    await this.numberSelector.targetNumbers(
      this.gameConfig.results.winningNumbers.map((res) => res.numberSelection),
    )

    await this.playExtraDrawAddOn()
    store.dispatch(AnimationActions.setPlayState(ANIMATION_PLAY_STATE.FINISHED))
  }

  getExtraAddOnDetails() {
    if (
      this.gameConfig.mechanicConfig.extraDrawAnimationConfig === null ||
      !this.gameConfig.results.extraDrawAddOnResult
    ) {
      return
    }

    return {
      ...this.gameConfig.results.extraDrawAddOnResult,
    }
  }

  async playExtraDrawAddOn() {
    if (this.extraDrawAnimation === null || this.skipToEnd) {
      return
    }

    await this.extraDrawAnimation.playTransitionEffect()
    await this.extraDrawAnimation.playNumberSelection()
  }

  getMultiplierAddOnDetails() {
    if (
      this.gameConfig.mechanicConfig.multiplierAnimationConfig === null ||
      !this.gameConfig.results.multiplierAddOnResult
    ) {
      return
    }

    return this.gameConfig.results.multiplierAddOnResult
  }

  setGameSpeed(gameSpeed: GAME_SPEED) {
    this.tweens.setGlobalTimeScale(gameSpeed)
  }

  findAllVideoObject() {}

  async skipAnimation() {
    if (this.skipToEnd) return
    this.skipToEnd = true

    this.multiplierAnimation?.skip()
    this.tweens.killAll()
    this.ballsRemainingDisplay?.skip()
    this.numberSelector?.skip(
      this.gameConfig.results.winningNumbers.map((res) => res.numberSelection),
    )
    this.extraDrawAnimation?.skip()

    store.dispatch(AnimationActions.setAddOnPanelState(true))
    store.dispatch(AnimationActions.setPlayState(ANIMATION_PLAY_STATE.FINISHED))
  }

  addToTopLayerNumberGridContainer(object: Phaser.GameObjects.GameObject) {
    this.topLayerNumberGridContainer?.add(object)
  }
}
